import React, { useEffect, useMemo, useState } from "react";
import Layout from "../../components/Layout";
import {
  FileText,
  Eye,
  Globe,
  TrendingUp,
  PlusCircle,
  BookOpen,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import "./../../styles/member/dashboard.css";
import { PostsService, type Post } from "../../services/postsService";
import { Link } from "react-router-dom";

interface StatCard {
  id: string;
  title: string;
  desc: string;
  number?: string | number;
  sub?: string;
  icon: React.ReactNode;
}

const Dashboard: React.FC = () => {
  const getStartedRef = React.useRef<HTMLAnchorElement>(null);
  const [recentPosts, setRecentPosts] = useState<Post[]>([]);
  const [total, setTotal] = useState<number>(0); // Lưu tổng số bài viết
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userStats, setUserStats] = useState<any | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAll, setShowAll] = useState(false); // Thêm trạng thái showAll

  const ITEMS_PER_PAGE = 5; // Mặc định hiển thị 5 bài

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [statsResp, postsResp] = await Promise.all([
          PostsService.getUserPostStats(),
          PostsService.getMyPosts({ page: 1, limit: showAll ? undefined : ITEMS_PER_PAGE }),
        ]);

        if (!isMounted) return;

        setUserStats(statsResp);
        setRecentPosts(postsResp.posts || []);
        setTotal(postsResp.total || 0); // Lưu tổng số bài viết
      } catch (e: any) {
        if (!isMounted) return;
        setError(e?.message || "Không thể tải dữ liệu dashboard");
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [refreshKey, showAll]); // Thêm showAll vào dependencies

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  // Hàm xử lý khi nhấn "Xem tất cả" hoặc "Rút gọn"
  const toggleShowAll = () => {
    setShowAll((prev) => !prev);
  };

    const computed = useMemo(() => {
    const backendApproved = userStats?.posts?.approved;
    const backendPending = userStats?.posts?.pending;
    const backendRejected = userStats?.posts?.rejected;
    const backendTotal = userStats?.posts?.total;
    const backendViews = userStats?.posts?.totalApprovedViews;
    const backendIndexed = userStats?.posts?.indexed;

    const approved = recentPosts.filter((p) => p.status === "approved");
    const sampleApprovedCount = approved.length;
    const samplePendingCount = recentPosts.filter(
      (p) => p.status === "pending"
    ).length;
    const sampleRejectedCount = recentPosts.filter(
      (p) => p.status === "rejected"
    ).length;
    const totalInSample = recentPosts.length || 1;
    const samplePerformancePercent = Math.round(
      (sampleApprovedCount / totalInSample) * 100
    );
    const sampleApprovedViews = approved.reduce(
      (sum, p) => sum + (typeof p.viewCount === "number" ? p.viewCount : 0),
      0
    );

    const approvedCount =
      typeof backendApproved === "number"
        ? backendApproved
        : sampleApprovedCount;
    const pendingCount =
      typeof backendPending === "number" ? backendPending : samplePendingCount;
    const rejectedCount =
      typeof backendRejected === "number"
        ? backendRejected
        : sampleRejectedCount;
    const performancePercent =
      typeof backendApproved === "number" &&
      typeof backendTotal === "number" &&
      backendTotal > 0
        ? Math.round((backendApproved / backendTotal) * 100)
        : samplePerformancePercent;
    const approvedViews =
      typeof backendViews === "number" ? backendViews : sampleApprovedViews;
    const indexedCount =
      typeof backendIndexed === "number" ? backendIndexed : 0;

    return {
      approvedCount,
      pendingCount,
      rejectedCount,
      performancePercent,
      approvedViews,
      indexedCount,
    };
  }, [recentPosts, userStats]);

  const stats: StatCard[] = [
    {
      id: "posts",
      title: "Tổng bài viết",
      desc: "Tổng số bài viết đã tạo",
      number: total,
      icon: <FileText size={24} />,
    },
    {
      id: "views",
      title: "Lượt xem",
      desc: "Tổng số lượt xem",
      number: computed.approvedViews,
      icon: <Eye size={24} />,
    },
    {
      id: "indexed",
      title: "Đã index",
      desc: "Tổng số đã được Google index",
      number: computed.indexedCount,
      icon: <Globe size={24} />,
    },
    {
      id: "performance",
      title: "Hiệu suất",
      desc: "Tỷ lệ bài viết được duyệt",
      number: `${computed.performancePercent}%`,
      icon: <TrendingUp size={24} />,
    },
  ];

  const mapStatusToBadge = (status: Post["status"]) => {
    switch (status) {
      case "approved":
        return { label: "Đã duyệt", cls: "published" };
      case "rejected":
        return { label: "Từ chối", cls: "reject" };
      default:
        return { label: "Đang chờ", cls: "pending" };
    }
  };

  const monthlyLimit = userStats?.limits?.monthly ?? null;
  const monthlyUsed = userStats?.usage?.monthly ?? 0;
  const packageName = userStats?.package?.name ?? "Miễn phí";
  const dailyLimit = userStats?.limits?.daily;
  const dailyRemaining = userStats?.remaining?.daily;
  const tokenBalance = userStats?.user?.tokenBalance;
  const userRole: string | undefined = userStats?.user?.role;
  const isUnlimited = userRole === "admin" || userRole === "moderator";

  const progressPercent = useMemo(() => {
    if (isUnlimited) return 0;
    if (monthlyLimit && typeof monthlyLimit === "number" && monthlyLimit > 0) {
      return Math.min(100, Math.round((monthlyUsed / monthlyLimit) * 100));
    }
    if (
      typeof dailyLimit === "number" &&
      dailyLimit > 0 &&
      typeof dailyRemaining === "number"
    ) {
      const used = dailyLimit - dailyRemaining;
      return Math.min(100, Math.round((used / dailyLimit) * 100));
    }
    return 0;
  }, [monthlyLimit, monthlyUsed, dailyLimit, dailyRemaining, isUnlimited]);

  return (
    <Layout>
      <div className="dashboard-page">
        {/* ===== HEADER ===== */}
        <div className="dashboard-header">
          <div>
            <h2>Dashboard</h2>
            <p>Tổng quan hệ thống quản lý bài viết</p>
          </div>
        </div>

        {/* ===== STATS CARDS ===== */}
        <div className="dashboard-stats">
          {stats.map((s) => (
            <div className="statics-card" key={s.id}>
              <div className="stat-info">
                <h3>{s.title}</h3>
                <div className="stat-icon">{s.icon}</div>
              </div>
              <div className="stat-statics">
                <p>{s.desc}</p>
                {s.number !== undefined && (
                  <p className="stat-number">{s.number}</p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="secion-container">
          {/* ===== SERVICE CARD ===== */}
          <div className="service-card">
            <div className="service-header-member">
              <div className="info-service">
                <h3>Gói dịch vụ hiện tại</h3>
                <span>Thông tin về gói dịch vụ và giới hạn sử dụng</span>
              </div>
              <span className="tag-service">{packageName}</span>
            </div>
            <div className="progress-info">
              {isUnlimited ? (
                <>
                  <span>Giới hạn:</span>
                  <span> Vô hạn</span>
                </>
              ) : monthlyLimit ? (
                <>
                  <span>Bài viết tháng này:</span>
                  <span>
                    {" "}
                    {monthlyUsed}/{monthlyLimit} bài viết
                  </span>
                </>
              ) : typeof dailyLimit === "number" && dailyLimit > 0 ? (
                <>
                  <span>Bài viết hôm nay (token):</span>
                  <span>
                    {typeof dailyRemaining === "number"
                      ? ` ${dailyLimit - dailyRemaining}/${dailyLimit} bài viết`
                      : " N/A"}
                  </span>
                </>
              ) : (
                <>
                  <span>Token hiện có:</span>
                  <span>
                    {" "}
                    {isUnlimited
                      ? "Vô hạn"
                      : typeof tokenBalance === "number"
                      ? tokenBalance
                      : "N/A"}
                  </span>
                </>
              )}
            </div>
            <div className="progress-bar-member">
              <div
                className="progress-member"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>

            <div className="features-list">
              <p>
                ✔{" "}
                {isUnlimited
                  ? "Vô hạn bài viết"
                  : monthlyLimit
                  ? `${monthlyLimit} bài viết/tháng`
                  : typeof dailyLimit === "number"
                  ? `Sử dụng token/ngày`
                  : `${
                      typeof tokenBalance === "number" ? tokenBalance : 0
                    } token còn lại`}
              </p>
              <p>✔ Duyệt thường</p>
              <p>✔ Hỗ trợ cơ bản</p>
            </div>
          </div>

          {/* ===== QUICK ACTIONS ===== */}
          <div className="quick-actions-container">
            <h3>Thao tác nhanh</h3>
            <div className="actions-grid">
              <Link
                to="/posts/create"
                className="action-button link-no-underline"
                ref={getStartedRef}
              >
                <PlusCircle size={18} /> Tạo bài viết mới
              </Link>
              <Link
                to="/posts/my-posts"
                className="action-button link-no-underline"
                ref={getStartedRef}
              >
                <BookOpen size={18} /> Quản lý bài viết
              </Link>
              <Link
                to="/news"
                className="action-button link-no-underline"
                ref={getStartedRef}
              >
                <ExternalLink size={18} /> Xem bài viết công khai
              </Link>
            </div>
          </div>
        </div>

        {/* ===== RECENT POSTS ===== */}
        <div className="recent-posts">
          <div className="recent-header">
            <h3>Bài viết gần đây</h3>
            {total > ITEMS_PER_PAGE ? (
              <button className="show-all-btn" onClick={toggleShowAll}>
                {showAll ? "Rút gọn" : "Xem tất cả"}
              </button>
            ) : (
              <Link to="/posts/my-posts">Xem tất cả</Link>
            )}
          </div>
          {isLoading ? (
            <div className="loading-state">
              <RefreshCw size={20} className="spinning" />
              <p>Đang tải dữ liệu...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>❌ {error}</p>
              <button onClick={handleRefresh} className="retry-btn">
                Thử lại
              </button>
            </div>
          ) : (
            <div className="post-list">
              {recentPosts.length === 0 ? (
                <p>Chưa có bài viết nào được tạo.</p>
              ) : (
                recentPosts.map((post, index) => {
                  const badge = mapStatusToBadge(post.status);
                  const showViews =
                    post.status === "approved" &&
                    typeof post.viewCount === "number" &&
                    post.viewCount > 0;
                  return (
                    <div key={index} className="post-item">
                      <div className="post-info">
                        <h4>{post.title}</h4>
                        {post.category?.name && (
                          <span>Danh mục: {post.category.name}</span>
                        )}
                      </div>
                      <div className={`post-status ${badge.cls}`}>
                        {showViews && (
                          <span style={{ marginRight: 8 }}>
                            {post.viewCount} lượt xem
                          </span>
                        )}
                        {badge.label}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;